import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            About Expected Value Club
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            A private online community where members share their best prediction market ideas — 
            opportunities where the market price and the fair price diverge.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-slate-800/50 rounded-xl p-8 md:p-10 space-y-8 backdrop-blur-sm border border-slate-700/50">
          
          {/* Introduction */}
          <section>
            <p className="text-lg text-slate-300 leading-relaxed">
              We focus on <span className="text-primary-400 font-semibold">expected value (EV)</span> — not luck. 
              Each submission must quantify the opportunity: what the market is pricing, what you believe the 
              fair price is, and why.
            </p>
          </section>

          <hr className="border-slate-700" />

          {/* How It Works */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-slate-300 mb-4">
              Membership is <span className="font-semibold text-white">highly selective</span>. 
              Access is granted in one of two ways:
            </p>
            <ol className="list-decimal list-inside space-y-3 text-slate-300 ml-4">
              <li>
                <span className="font-semibold text-white">Apply for membership</span> — submit an idea 
                that demonstrates strong reasoning, clear data, and an identifiable edge.
              </li>
              <li>
                <span className="font-semibold text-white">Earn entry</span> — publish a qualifying idea 
                that meets our review standards.
              </li>
            </ol>
            <p className="text-slate-300 mt-4">
              There's no cost to apply, but only a limited number of applicants are accepted. 
              Our goal is to maintain a high-quality community where ideas are statistically grounded 
              and intellectually rigorous.
            </p>
          </section>

          <hr className="border-slate-700" />

          {/* Idea Lifecycle */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Idea Lifecycle</h2>
            <p className="text-slate-300 mb-4">
              Each idea submitted to EVC follows a transparent, auditable lifecycle:
            </p>
            <div className="space-y-4">
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <h3 className="text-lg font-semibold text-primary-400 mb-2">1. Draft / Submitted</h3>
                <p className="text-slate-300 mb-2">Members propose an idea, including:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-300 ml-4">
                  <li>Market link (e.g., Kalshi, Polymarket)</li>
                  <li>Recommended position (buy/sell)</li>
                  <li>Fair price estimate</li>
                  <li>Thesis and supporting evidence</li>
                </ul>
              </div>

              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <h3 className="text-lg font-semibold text-primary-400 mb-2">2. Review</h3>
                <p className="text-slate-300">
                  Moderators vet ideas for quality and duplicates.
                </p>
              </div>

              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <h3 className="text-lg font-semibold text-primary-400 mb-2">3. Published (Live)</h3>
                <p className="text-slate-300">
                  Approved ideas are visible to members and begin performance tracking.
                </p>
              </div>

              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <h3 className="text-lg font-semibold text-primary-400 mb-2">4. Archived</h3>
                <p className="text-slate-300">
                  Once a market converges or expires, final results are recorded and performance is locked.
                </p>
              </div>
            </div>
            <p className="text-slate-300 mt-4">
              Every published idea contributes to your <span className="font-semibold text-white">track record</span> — 
              measured in cumulative ROI points.
            </p>
          </section>

          <hr className="border-slate-700" />

          {/* Member Scoring */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Member Scoring and Track Records</h2>
            <p className="text-slate-300 mb-4">
              Members are ranked by their <span className="font-semibold text-white">Cumulative Unitized ROI</span>, 
              based on the performance of their published ideas.
            </p>
            
            <div className="bg-slate-900/50 p-6 rounded-lg border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-3">Example:</h3>
              <div className="space-y-2 text-slate-300">
                <div className="flex justify-between">
                  <span>Idea #1:</span>
                  <span className="text-green-400 font-semibold">+50%</span>
                </div>
                <div className="flex justify-between">
                  <span>Idea #2:</span>
                  <span className="text-green-400 font-semibold">+200%</span>
                </div>
                <div className="flex justify-between">
                  <span>Idea #3:</span>
                  <span className="text-red-400 font-semibold">−50%</span>
                </div>
                <hr className="border-slate-700 my-2" />
                <div className="flex justify-between font-bold text-white text-lg">
                  <span>Total ROI Points:</span>
                  <span className="text-green-400">+200</span>
                </div>
              </div>
            </div>

            <p className="text-slate-300 mt-4">
              This approach rewards accuracy and consistency over time, not volume. Members can view their 
              historical performance, active ideas, and archived outcomes directly from their dashboard.
            </p>
          </section>

          <hr className="border-slate-700" />

          {/* Why Join */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Why Join</h2>
            <p className="text-slate-300 mb-4">
              Traditional forums and chat groups around prediction markets tend to reward noise, not signal. 
              Expected Value Club is different — it's a <span className="font-semibold text-white">merit-based 
              forum for thoughtful participants</span> who care about modeling, reasoning, and calibration.
            </p>
            
            <div className="bg-slate-900/50 p-6 rounded-lg border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-3">Members include:</h3>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start">
                  <span className="text-primary-400 mr-2">•</span>
                  <span>Data scientists, engineers, and analysts who value quantifiable edge</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-400 mr-2">•</span>
                  <span>Economists, traders, and policy thinkers exploring real-world probabilities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-400 mr-2">•</span>
                  <span>Competitive forecasters looking for a serious, high-signal environment</span>
                </li>
              </ul>
            </div>

            <p className="text-slate-300 mt-4">
              Every idea is a chance to test your reasoning, build a measurable track record, and engage 
              with others who think rigorously about uncertainty.
            </p>
          </section>

          <hr className="border-slate-700" />

          {/* Club Principles */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Club Principles</h2>
            <div className="space-y-3">
              <div className="flex items-start">
                <span className="text-primary-400 font-bold mr-3 text-xl">1.</span>
                <div>
                  <span className="font-semibold text-white">Quality over quantity</span>
                  <span className="text-slate-300"> — submit only your best ideas.</span>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-primary-400 font-bold mr-3 text-xl">2.</span>
                <div>
                  <span className="font-semibold text-white">Transparency</span>
                  <span className="text-slate-300"> — every trade idea includes a clear fair value and reasoning.</span>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-primary-400 font-bold mr-3 text-xl">3.</span>
                <div>
                  <span className="font-semibold text-white">Respect the market</span>
                  <span className="text-slate-300"> — no duplicate or plagiarized ideas.</span>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-primary-400 font-bold mr-3 text-xl">4.</span>
                <div>
                  <span className="font-semibold text-white">No trading conflicts</span>
                  <span className="text-slate-300"> — moderators cannot trade markets they review.</span>
                </div>
              </div>
            </div>
          </section>

          <hr className="border-slate-700" />

          {/* Join CTA */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Join Expected Value Club</h2>
            <p className="text-slate-300 mb-6">
              To apply, submit a current prediction market opportunity with a clear thesis, fair value estimate, 
              and supporting logic. Applications are reviewed on the strength of your reasoning and understanding 
              of market mechanics.
            </p>
            
            <div className="bg-gradient-to-r from-primary-600 to-primary-500 p-6 rounded-lg text-center">
              <p className="text-white text-lg font-semibold mb-4">
                Expected Value Club — where probability meets performance.
              </p>
              <Link
                to="/register"
                className="inline-block bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
              >
                Apply for Membership
              </Link>
            </div>
          </section>

          {/* Footer Note */}
          <div className="text-center text-sm text-slate-400 pt-4">
            <p>
              © 2025 ExpectedValueClub.com — All rights reserved.
            </p>
            <p className="mt-1">
              Registration or use of this site constitutes acceptance of our <Link to="/terms" className="text-primary-500 hover:text-primary-600 underline">Terms and Conditions</Link>.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default AboutPage;

